/**
 * План до пилота для страницы «Ход разработки»: контрольные точки ускоренного календаря владельца (31.10.2026,
 * раздел 0 docs/tz-v2/plan.md), чаты разработки и их разделы (docs/chats/README.md), шаги владельца
 * (docs/tz-v2/owner.json). Критерии приёмки ПМ-01…ПМ-14 и их статусы берутся из реестра требований, здесь — только
 * какие чаты их ведут. Состояние точки (прошла, идёт, впереди) считается по текущей дате, руками не пишется.
 */
import { REQUIREMENTS, type Requirement } from './requirements';

/** Редакция ТЗ, по которой ведётся работа */
export const TZ_EDITION = {
  title: 'ТЗ v2.0',
  date: '2026-10-29',
  calendar: '2026-10-31',
  /** Где тексты требований */
  texts: 'тексты требований — в Google Docs, части 1–3',
} as const;

export interface PilotMilestone {
  id: string;
  /** Начало (ГГГГ-ММ-ДД) */
  from: string;
  /** Конец, если точка — промежуток */
  to?: string;
  title: string;
  /** Что за этим стоит: шаги владельца, критерии */
  note?: string;
}

/** Контрольные точки до решения о пилоте — по порядку дат */
export const PILOT_MILESTONES: PilotMilestone[] = [
  {
    id: 'start',
    from: '2026-11-01',
    title: 'Старт работ: обе волны чатов сразу',
    note: 'девять рабочих чатов и главный; порядок задач и критерии ТЗ v2.0 не меняются, сдвинуты только даты',
  },
  {
    id: 'owner-data-1',
    from: '2026-11-04',
    to: '2026-11-06',
    title: 'Решения и данные владельца, первая часть',
    note: 'шаги 0.4, 0.5, 0.9, 0.15–0.17: от них зависят эталоны ПМ-01, ПМ-02 и ПМ-04',
  },
  {
    id: 'owner-data-2',
    from: '2026-11-13',
    title: 'Данные владельца, вторая часть; пилотные прораб и представитель заказчика',
    note: 'шаги 0.6–0.8, 0.10, 0.11, 0.13 и 0.18',
  },
  {
    id: 'site-stats',
    from: '2026-11-16',
    to: '2026-12-19',
    title: 'Площадка копит статистику для ПМ-05, ПМ-09 и ПМ-14',
    note: 'не меньше четырёх недель реальных отчётов до итоговой проверки — кодом это не ускорить',
  },
  {
    id: 'routes-freeze',
    from: '2026-11-27',
    title: 'Заморозка маршрутов для внешнего теста на проникновение (ПМ-07)',
  },
  {
    id: 'pentest-report',
    from: '2026-12-11',
    title: 'Отчёт внешнего теста на проникновение',
  },
  {
    id: 'final-check',
    from: '2026-12-15',
    to: '2026-12-19',
    title: 'Итоговая проверка 14 критериев приёмки на одной версии',
  },
  {
    id: 'pilot-decision',
    from: '2026-12-21',
    title: 'Решение о старте пилота',
  },
];

export type MilestoneState = 'past' | 'now' | 'ahead';

export const MILESTONE_STATE_TITLES: Record<MilestoneState, string> = {
  past: 'прошло',
  now: 'идёт',
  ahead: 'впереди',
};

/** Сегодня по времени Казахстана (UTC+5, ГГГГ-ММ-ДД) — сроки плана заданы по нему */
export function todayKz(at: Date = new Date()): string {
  return new Date(at.getTime() + 5 * 3_600_000).toISOString().slice(0, 10);
}

/** Прошла точка, идёт или впереди — по дате (ГГГГ-ММ-ДД); последний день промежутка ещё «идёт» */
export function milestoneState(m: PilotMilestone, today: string): MilestoneState {
  if (today < m.from) return 'ahead';
  if (today > (m.to ?? m.from)) return 'past';
  return 'now';
}

export interface DevChat {
  code: string;
  title: string;
  /** Разделы ТЗ */
  sections: string;
  /** Критерии приёмки, которые ведёт чат */
  criteria: string[];
  /** Волна: 0 — главный чат */
  wave: 0 | 1 | 2;
}

/** Чаты разработки с 28.10.2026 (docs/chats/README.md); с 1.11 обе волны работают одновременно */
export const DEV_CHATS: DevChat[] = [
  { code: 'main', title: 'Главный', sections: 'выкладка, документы, план работ', criteria: [], wave: 0 },
  { code: 'plan', title: 'График и ресурсы', sections: '6.2, 6.3', criteria: ['ПМ-03', 'ПМ-08'], wave: 1 },
  { code: 'help', title: 'Помощь и предложения', sections: '6.22', criteria: ['ПМ-11'], wave: 1 },
  {
    code: 'common',
    title: 'Общее: вход, права, безопасность',
    sections: '6.23, 8.2, 8.4, общий интерфейс',
    criteria: ['ПМ-07', 'ПМ-08', 'ПМ-12'],
    wave: 1,
  },
  {
    code: 'est',
    title: 'Сметы и бюджет',
    sections: '6.5, 6.6',
    criteria: ['ПМ-01', 'ПМ-02', 'ПМ-08'],
    wave: 1,
  },
  {
    code: 'proc',
    title: 'Закупки, договоры и склады',
    sections: '6.7, 6.8, 6.12, 6.13',
    criteria: ['ПМ-04', 'ПМ-13'],
    wave: 1,
  },
  {
    code: 'site',
    title: 'Площадка и телефон',
    sections: '6.9, 6.15, 6.16, 6.25',
    criteria: ['ПМ-05', 'ПМ-09', 'ПМ-14'],
    wave: 1,
  },
  { code: 'fin', title: 'Финансы', sections: '6.14', criteria: ['ПМ-04'], wave: 2 },
  {
    code: 'joints',
    title: 'Надзор, ПСД и изменения',
    sections: '6.4, 6.10, 6.11, кабинеты надзора и заказчика в 6.19',
    criteria: ['ПМ-13'],
    wave: 2,
  },
  { code: 'ai', title: 'Pilot (ИИ)', sections: '6.17', criteria: ['ПМ-06', 'ПМ-10'], wave: 2 },
];

/** Критерии приёмки первой очереди ПМ-01…ПМ-14 из реестра, по номеру */
export function pilotCriteria(): Requirement[] {
  return REQUIREMENTS.filter((r) => /^ПМ-\d+$/.test(r.code)).sort((a, b) => a.code.localeCompare(b.code));
}

/** Какие чаты ведут критерий */
export function chatsForCriterion(code: string): DevChat[] {
  return DEV_CHATS.filter((c) => c.criteria.includes(code));
}

export interface OwnerStep {
  /** Номер шага нулевого этапа ТЗ v2.0 */
  step: string;
  title: string;
  /** Срок (ГГГГ-ММ-ДД) */
  due: string;
  /** Дата, когда шаг сделан; пока не сделан — пусто */
  doneAt?: string;
}

/**
 * Шаги владельца (docs/tz-v2/owner.json, на доске — чат owner). Сделанный шаг отмечается датой в doneAt и уходит
 * со страницы. Только названия и сроки — без имён, адресов и ключей.
 */
export const OWNER_STEPS: OwnerStep[] = [
  { step: '0.1', title: 'Открыть доску задач основному аккаунту и чатам', due: '2026-11-02' },
  {
    step: 'В-1, В-2',
    title:
      'Порядок работы: кто сливает изменения других исполнителей, единые правила, нумерация карточек задач',
    due: '2026-11-02',
  },
  {
    step: '0.4',
    title: 'Ставки 21,5 / 10 / 3 %, правило округления, выпуск цен июля 2026',
    due: '2026-11-04',
  },
  {
    step: '0.9',
    title: 'Ключи внешних сервисов: бот Telegram, контакт платформы, проверка файлов на вирусы',
    due: '2026-11-04',
  },
  {
    step: '0.5',
    title:
      '13 смет KENML стройки 227-23, выгрузки АВС, бюджет-пакет из 7 файлов; разрешение хранить обезличенные эталоны',
    due: '2026-11-06',
  },
  {
    step: '0.15',
    title:
      'Заключение юриста о персональных данных пилота на Railway: перенос в РК либо согласия и минимизация',
    due: '2026-11-06',
  },
  {
    step: '0.16',
    title: '91 требование первой очереди без чата: назначить или перенести во вторую',
    due: '2026-11-06',
  },
  { step: '0.17', title: 'Объём SIGNAL до пилота, месячный бюджет ИИ и подписки', due: '2026-11-06' },
  {
    step: '0.2, 0.3',
    title: 'Подтвердить стек письменно; решения по ДГ-19, НФ-16, НФ-22, ЗК-29 и способу подписи',
    due: '2026-11-06',
  },
  {
    step: '0.6–0.8',
    title:
      'Эталонные графики XER и XML; шаблоны ИД и журналов, техника и нормы топлива; доходный договор 227-23',
    due: '2026-11-13',
  },
  {
    step: '0.10, 0.11',
    title: 'Роли подразделений и уровни денег; почта, домен и юридические страницы портала',
    due: '2026-11-13',
  },
  {
    step: '0.13',
    title: 'Заказать внешний тест на проникновение и проверку ASVS уровня 2',
    due: '2026-11-13',
  },
  {
    step: 'В-3…В-6',
    title:
      'Нужен ли перенос из SIGNAL; набор документов для ПМ-10; источник проверки контрагентов, выписки и 1С; папка норм',
    due: '2026-11-13',
  },
  {
    step: '0.18',
    title: 'Пилотные прораб и представитель заказчика; приложение через браузер или магазины приложений',
    due: '2026-11-13',
  },
];

/** Шаги владельца, которые ещё не сделаны, по сроку */
export function openOwnerSteps(): OwnerStep[] {
  return OWNER_STEPS.filter((s) => !s.doneAt).sort((a, b) => (a.due < b.due ? -1 : a.due > b.due ? 1 : 0));
}
